// SPA GS Gestion — app HYBRIDE depuis E1a (MASTER-GEST-E1A-ACCUEIL, mocks figes
// David : desktop + mobile 1re classe) : GsSidebar (>=md, 3 zones §4c — sections
// nommees « General » + « Comite de gestion ») ET GsMobileBottomNav (<md, 4
// onglets max §17 : Accueil · Resultats · Equipes · Plus → bottom sheet).
// Layout @gs/ui, pattern pilote admin/ug/inv (compile contre la vraie API
// @gs/ui/@gs/auth/@gs/api-client). registerApiAuth DANS le rendu (synchrone).
// Garde CLIENT (hasRole) = UX ; l'autorite reelle est serveur (requireRole
// bc_key GEST sur ug.UserCompanyBcRoles).
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  GsLayout, GsHeader, GsSidebar, GsMobileBottomNav, useDarkMode, GsErrorBoundary,
  GsErrorPage, GsLoginScreen, BrandingProvider, GsNotificationBell, GsFeedbackButton,
  GsTooltipProvider, GS_COMPONENTS,
} from '@gs/ui';
import type { SidebarItem } from '@gs/ui';
import {
  Home, MessageSquare, Briefcase, UsersRound, BarChart3, Users, Network,
  ClipboardList, CalendarDays, Menu,
} from 'lucide-react';
import { useAuth, GsRole } from '@gs/auth';
import { registerApiAuth, clearApiAuth, ApiForbiddenError } from '@gs/api-client';
import { useFeedbackUnreadCount } from '@gs/feedback/client';
import { useTranslation } from './i18n';
import type { TFunction } from 'i18next';
import { useMe, isManagementMe } from './hooks/useMe';
import { useCorporateContext, useSetContext } from './hooks/useCorporateContext';
import { MoreMenuSheet } from './components/MoreMenuSheet';
import type { MoreMenuItem } from './components/MoreMenuSheet';

import AccueilView from './pages/accueil/AccueilView';
import ComingSoonPage from './pages/ComingSoonPage';
import EquiteSalarialePage from './pages/planification/EquiteSalarialePage';
import PlanificationPage from './pages/planification/PlanificationPage';
import FeedbackView from './pages/FeedbackView';
import ComitesPage from './pages/comites/ComitesPage';
import CommitteeDetailPage from './pages/comites/CommitteeDetailPage';
import EquipesPage from './pages/equipes/EquipesPage';
import TeamDetailPage from './pages/equipes/TeamDetailPage';
import AffichagesPage from './pages/affichages/AffichagesPage';
import OrganigrammePage from './pages/organigramme/OrganigrammePage';
import PostingDetailPage from './pages/affichages/PostingDetailPage';
import ResultatsFinanciersPage from './pages/finance/ResultatsFinanciersPage';
import FinanceDetailPage from './pages/finance/FinanceDetailPage';
import MyProfilePage from './pages/profile/MyProfilePage';
import MemberProfilePage from './pages/profile/MemberProfilePage';
import MonEquipePage from './pages/mon-equipe/MonEquipePage';
import SanteEquipePage from './pages/mon-equipe/SanteEquipePage';
import MonEquipeMemberRedirect from './pages/mon-equipe/MonEquipeMemberRedirect';

function pageTitle(pathname: string, t: TFunction): string {
  if (pathname.startsWith('/commentaires')) return t('gest:pageTitle.feedback');
  if (pathname.startsWith('/profile')) return t('gest:pageTitle.profile');
  if (pathname.startsWith('/comites')) return t('gest:pageTitle.comites');
  if (pathname.startsWith('/resultats')) return t('gest:pageTitle.resultats');
  // Avant /equipes : « /mon-equipe » ne doit pas etre happe par un futur prefixe.
  if (pathname.startsWith('/mon-equipe')) return t('gest:pageTitle.monEquipe');
  if (pathname.startsWith('/equipes')) return t('gest:pageTitle.equipes');
  if (pathname.startsWith('/organigramme')) return t('gest:pageTitle.organigramme');
  if (pathname.startsWith('/affichages')) return t('gest:pageTitle.affichages');
  if (pathname.startsWith('/rencontres')) return t('gest:pageTitle.rencontres');
  return t('gest:pageTitle.home');
}

/** Sentinelle « onglet Plus » de la barre mobile — ouvre le bottom sheet, ne navigue pas. */
const MORE_PATH = '#plus';

// FRAG2 : le role affiche vient du Systeme C (GET /api/me — ug.UserCompanyBcRoles
// bc_key GEST), JAMAIS des groupes Azure AD. Fallback Systeme B le temps que /api/me
// reponde (etat de chargement seulement).
function systemCRoleLabel(role: 'UTILISATEUR' | 'GESTIONNAIRE' | 'ADMIN' | null | undefined, fallback: GsRole, t: TFunction): string {
  switch (role ?? (fallback === GsRole.ADMIN ? 'ADMIN' : fallback === GsRole.GESTIONNAIRE ? 'GESTIONNAIRE' : 'UTILISATEUR')) {
    case 'ADMIN': return t('gest:roles.admin');
    case 'GESTIONNAIRE': return t('gest:roles.manager');
    default: return t('gest:roles.user');
  }
}

type AccessGateKind = 'notProvisioned' | 'moduleNotEntitled' | 'noRole';

/**
 * Ecran bloquant « acces via votre administrateur » (FRAG2, provisionOnMiss:false).
 * L'autorite reelle est SERVEUR (403 des portes requireBc) — cet ecran est l'UX
 * du refus. touch-manipulation + card blanche DS (jamais de card coloree).
 */
function AccessGateScreen({ kind, email, onLogout, t }: {
  kind: AccessGateKind; email: string; onLogout: () => void; t: TFunction;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-page p-4 dark:bg-slate-900">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-sm dark:bg-slate-900">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-950">
          <Briefcase className="h-6 w-6 text-brand-600" aria-hidden />
        </div>
        <h1 className="mb-2 text-head font-semibold text-slate-900 dark:text-slate-100">
          {t('gest:authGate.title')}
        </h1>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">
          {t(`gest:authGate.${kind}`)}
        </p>
        <p className="mb-6 text-xs text-slate-400 dark:text-slate-500">
          {t('gest:authGate.signedInAs', { email })}
        </p>
        <button
          type="button"
          onClick={onLogout}
          className={`${GS_COMPONENTS.btnActionMobile} touch-manipulation`}
        >
          {t('gest:authGate.logout')}
        </button>
      </div>
    </div>
  );
}

/** Mappe l'etat /api/me sur l'ecran de refus (null = acces OK ou statut inconnu fail-soft). */
function resolveAccessGate(me: ReturnType<typeof useMe>): AccessGateKind | null {
  if (me.isError) {
    const body = me.error instanceof ApiForbiddenError ? (me.error.originalBody as { code?: string } | undefined) : undefined;
    if (body?.code === 'GEST_USER_NOT_PROVISIONED') return 'notProvisioned';
    // Autres erreurs (transitoire/serveur) : fail-soft — les portes serveur restent l'autorite.
    return null;
  }
  if (!me.data) return null;
  // Miroir EXACT des portes serveur : PORTE 1 (entitlement) SANS bypass corporate
  // (licence par compagnie, ADR-067 D1) ; PORTE 2 (role) bypassee par corporate.
  if (!me.data.moduleEntitled) return 'moduleNotEntitled';
  if (!me.data.role && !me.data.isCorporateAdmin) return 'noRole';
  return null;
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const { user, isLoading, login, logout, getAccessToken } = useAuth();
  const { t } = useTranslation();

  // Enregistrement SYNCHRONE du token (dans le rendu) — avant que TanStack Query monte.
  if (user) {
    registerApiAuth(getAccessToken);
  } else {
    clearApiAuth();
  }
  useEffect(() => () => { clearApiAuth(); }, []);

  // Badge non-lus « Commentaires » (capability-aware : 404/503 => pas de badge).
  const unread = useFeedbackUnreadCount(!!user);

  // Statut d'acces Systeme C (FRAG2) : entitlement etage 1 + role etage 2 reels.
  const me = useMe(!!user);
  const management = isManagementMe(me.data);

  // Selecteur de contexte A3 (ACCESS-CONTROL-STANDARD §5) : visible SEULEMENT si le
  // SERVEUR resout l'user corporate (grant UCA/ADM). La bascule ne fait que poser le
  // company_id de session ; gest garde ses portes fermees (pas de god-path).
  const corporateContext = useCorporateContext(!!user);
  const setContext = useSetContext();

  // Tiroir « Plus » de la barre mobile (bottom sheet, §17 : 4 onglets max).
  const [moreOpen, setMoreOpen] = useState(false);

  // Zone METIER (mock fige E1a) : Accueil en tete (actif, sans libelle de
  // section) PUIS sections nommees « General » (Resultats financiers · Equipes ·
  // Organigramme · Affichages — ecrans « a venir » aux frags suivants) et
  // « Comite de gestion » (Rencontres CG · Comites — management seulement,
  // M-402 : affordance nav, l'autorite reste les gardes serveur).
  const navItems: SidebarItem[] = [
    { key: 'home', label: t('gest:nav.home'), icon: Home, path: '/' },
  ];
  const generalItems: SidebarItem[] = [
    { key: 'resultats', label: t('gest:nav.resultats'), icon: BarChart3, path: '/resultats-financiers' },
    { key: 'equipes', label: t('gest:nav.equipes'), icon: Users, path: '/equipes' },
    { key: 'organigramme', label: t('gest:nav.organigramme'), icon: Network, path: '/organigramme' },
    { key: 'affichages', label: t('gest:nav.affichages'), icon: ClipboardList, path: '/affichages' },
  ];
  const cgItems: SidebarItem[] = [
    { key: 'rencontres', label: t('gest:nav.rencontres'), icon: CalendarDays, path: '/rencontres' },
    { key: 'comites', label: t('gest:nav.comites'), icon: UsersRound, path: '/comites' },
  ];

  // Zone SUPPORT (UI_STANDARDS §4c) : le transverse ne se melange JAMAIS au metier —
  // « Commentaires » (module @gs/feedback) vit en bas de la sidebar, derriere le divider.
  const supportItems: SidebarItem[] = [
    { key: 'feedback', label: t('gest:nav.feedback'), icon: MessageSquare, path: '/commentaires', badge: unread.data?.count },
  ];

  // Barre mobile (§17, mock mobile fige) : Accueil · Resultats · Equipes · Plus.
  const mobileItems: SidebarItem[] = [
    { key: 'home', label: t('gest:nav.home'), icon: Home, path: '/' },
    { key: 'resultats', label: t('gest:nav.resultatsShort'), icon: BarChart3, path: '/resultats-financiers' },
    { key: 'equipes', label: t('gest:nav.equipes'), icon: Users, path: '/equipes' },
    { key: 'more', label: t('gest:nav.more'), icon: Menu, path: MORE_PATH },
  ];

  // Tiroir « Plus » : le surplus de nav (mock mobile) — Comites = management.
  const moreItems: MoreMenuItem[] = [
    { key: 'organigramme', label: t('gest:nav.organigramme'), icon: Network, path: '/organigramme' },
    { key: 'affichages', label: t('gest:nav.affichages'), icon: ClipboardList, path: '/affichages' },
    { key: 'rencontres', label: t('gest:nav.rencontres'), icon: CalendarDays, path: '/rencontres' },
    ...(management
      ? [{ key: 'comites', label: t('gest:nav.comites'), icon: UsersRound, path: '/comites' } satisfies MoreMenuItem]
      : []),
    { key: 'feedback', label: t('gest:nav.feedback'), icon: MessageSquare, path: '/commentaires', badge: unread.data?.count },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <GsLoginScreen
        appName="GS Gestion"
        message={t('gest:login.message')}
        loginLabel={t('gest:login.cta')}
        onLogin={() => void login()}
        icon={Briefcase}
      />
    );
  }

  // Gate d'acces FRAG2 (provisionOnMiss:false) : non provisionne / module non
  // active / aucun role -> ecran « acces via votre administrateur ». Fail-soft
  // sur erreur transitoire (les portes serveur restent l'autorite).
  const accessGate = resolveAccessGate(me);
  if (accessGate) {
    return <AccessGateScreen kind={accessGate} email={user.email} onLogout={() => void logout()} t={t} />;
  }

  const bell = (
    <GsNotificationBell
      enabled={!!user}
      onNavigate={(n) => {
        if (n.refType !== 'FEEDBACK') return;
        navigate(n.refId ? `/commentaires?ticket=${n.refId}` : '/commentaires');
      }}
      labels={{
        bell: t('gest:notifications.label'),
        empty: t('gest:notifications.empty'),
        markAllRead: t('gest:notifications.markAllRead'),
        kindLabels: {
          FEEDBACK_CREATED: t('gest:notifications.kindFeedbackCreated'),
          FEEDBACK_MESSAGE: t('gest:notifications.kindFeedbackMessage'),
          FEEDBACK_INTERNAL_NOTE: t('gest:notifications.kindFeedbackInternalNote'),
        },
      }}
    />
  );

  // Cluster chrome : cloche a gauche, bulle Commentaires a droite (pattern inv/ug).
  const chrome = (
    <>
      {bell}
      <GsFeedbackButton
        enabled={!!user}
        onNavigate={() => navigate('/commentaires')}
        labels={{ button: t('gest:nav.feedback') }}
      />
    </>
  );

  return (
    // Pilote ADR-059 : theming runtime de la company de l'user. Fail-soft : fallback :root GS.
    <BrandingProvider enabled={!!user}>
      <GsLayout
        header={<GsHeader pageTitle={pageTitle(location.pathname, t)} trailingSlot={chrome} />}
        mobileNav={
          <GsMobileBottomNav
            items={mobileItems}
            activePath={location.pathname}
            navLabel={t('gest:nav.mobileNav')}
            onNavigate={(path: string) => {
              if (path === MORE_PATH) { setMoreOpen(true); return; }
              navigate(path);
            }}
          />
        }
        sidebar={
          // 3 zones §4c (mock fige) : Accueil sans libelle, puis sections nommees —
          // « General » via toolItems, « Comite de gestion » via adminItems (gate =
          // visibilite management, pas « admin » : la section CG est celle de la gestion).
          <GsSidebar
            variant="light"
            headerLogoSrc="/logo-gs.png"
            headerLogoSrcDark="/logo-gs-white.png"
            homeLabel={t('gest:chrome.backToHome')}
            appName="GS Gestion"
            items={navItems}
            contextSwitcher={
              corporateContext.data?.isCorporateAdmin
                ? {
                    currentCompanyId: corporateContext.data.currentCompanyId,
                    companies: corporateContext.data.companies,
                    onSelect: (companyId: string) => setContext.mutate(companyId),
                    label: t('gest:context.label'),
                    homeOptionLabel: t('gest:context.home'),
                    busy: setContext.isPending,
                  }
                : undefined
            }
            toolItems={generalItems}
            toolSectionLabel={t('gest:nav.sectionGeneral')}
            adminItems={cgItems}
            adminSectionLabel={t('gest:nav.sectionCg')}
            supportItems={supportItems}
            isAdmin={management}
            activePath={location.pathname}
            onNavigate={(path: string) => navigate(path)}
            bottomUser={{
              name: user.name,
              email: user.email,
              role: systemCRoleLabel(me.data?.role, user.role, t),
              initials: user.initials,
            }}
            isDark={isDark}
            onToggleDark={toggleDark}
            onLogout={() => void logout()}
            appVersion="0.1.0"
            collapseLabel={t('gest:chrome.collapseSidebar')}
            expandLabel={t('gest:chrome.expandSidebar')}
            userMenuLabels={{
              lightMode: t('gest:chrome.lightMode'),
              darkMode: t('gest:chrome.darkMode'),
              language: t('gest:chrome.language'),
              logout: t('gest:chrome.logout'),
            }}
          />
        }
      >
        <GsTooltipProvider>
          <GsErrorBoundary>
            <Routes>
              {/* E1a : accueil = tableau de bord social (remplace le placeholder scaffold). */}
              <Route path="/" element={<AccueilView />} />
              {/* E2 Resultats financiers : le SOMMAIRE porte l'item de nav — il est
                  ouvert a tout role GEST (employee inclus, F-503), la nav ne pointe
                  jamais un 403. Le DETAIL (visuels/tableau) est ADMIN seul (M-500) et
                  s'atteint par le lien F-504 du sommaire. L'inversion des URL vs PoC
                  (/finance = detail) est voulue. Import CSV + saisie = frag E2b. */}
              <Route path="/resultats-financiers" element={<ResultatsFinanciersPage />} />
              <Route path="/resultats-financiers/detail" element={<FinanceDetailPage />} />
              {/* Compat : /resultats etait le placeholder « a venir » et reste la cible
                  du drill-in « Indicateurs » de l'accueil (E1a) — redirige, jamais 404. */}
              <Route path="/resultats" element={<Navigate to="/resultats-financiers" replace />} />
              {/* Modules « a venir » (frags suivants) — routes honnetes derriere les items de nav du mock. */}
              {/* E1b Equipes : liste (DataTable + drill-in) + fiche ROUTEE (URL /equipes/{id}). */}
              <Route path="/equipes" element={<EquipesPage />} />
              <Route path="/equipes/:id" element={<TeamDetailPage />} />
              {/* L10 Mon equipe : roster + sante. Garde M-150 = director|admin
                  SEULEMENT (manager et employee -> 403 serveur -> ecran de refus) —
                  d'ou l'ABSENCE d'item de nav : la sidebar ne connait que
                  `management` (manager inclus), donc un item pointerait un 403 pour
                  les managers, ce que le parc s'interdit (cf. E2 : « la nav ne
                  pointe jamais un 403 »). La qualite de directeur est derivee des
                  donnees (gest.Teams), le client ne peut pas la resoudre seul.
                  L'entree se fait par URL/deep-link ; l'item de nav viendra quand
                  /api/me exposera la qualite de directeur. L'ordre compte —
                  /mon-equipe/sante doit preceder /mon-equipe/:email, sinon « sante »
                  serait lu comme un email (F-167). */}
              <Route path="/mon-equipe" element={<MonEquipePage />} />
              <Route path="/mon-equipe/sante" element={<SanteEquipePage />} />
              {/* F-167 : deep-link legacy -> /profile/{email}?tab=gestion (la fiche
                  employe vit sous /profile ; ses 28 actions sont L09, hors lot). */}
              <Route path="/mon-equipe/:email" element={<MonEquipeMemberRedirect />} />
              {/* E1c Organigramme : arbre interactif (zoom/pan, filtre, recherche) +
                  exports PNG/PDF. Lecture ouverte a tout role GEST (employee inclus). */}
              <Route path="/organigramme" element={<OrganigrammePage />} />
              {/* E1d Affichages : liste (DataTable + drill-in) + fiche ROUTEE (URL /affichages/{id}). */}
              <Route path="/affichages" element={<AffichagesPage />} />
              <Route path="/affichages/:id" element={<PostingDetailPage />} />
              <Route path="/rencontres" element={<ComingSoonPage moduleKey="meetings" />} />
              {/* E3a Comites : liste + fiche ROUTEE (drill-in, URL /comites/{id}). */}
              <Route path="/comites" element={<ComitesPage />} />
              <Route path="/comites/:id" element={<CommitteeDetailPage />} />
              {/* L16 Profil : SA fiche (/profile — onglets fixes, F-801) et la
                  fiche d'un membre (/profile/:email — onglets calcules par les 4
                  can* serveur, M-801). M-800 : viser son propre email redirige
                  vers /profile en preservant ?tab=. L'ordre compte — /profile doit
                  preceder /profile/:email. */}
              <Route path="/profile" element={<MyProfilePage />} />
              <Route path="/profile/:email" element={<MemberProfilePage />} />
              {/* L05 Planification salariale (F-601..611) : grille director
                  interactive (deplacement d'echelon/classe, scenarios CE, co.t reel,
                  PDF). Garde M-651 = director|admin SEULEMENT (manager ET employee ->
                  403 serveur -> /error/403) — d'ou l'ABSENCE d'item de nav (meme
                  raison que /mon-equipe : la sidebar ne connait que `management`, un
                  item pointerait un 403 pour les managers). L'entree se fait par
                  deep-link (KPI masse F-158 de Mon equipe, ?team=&from=mon-equipe).
                  L'ordre : apres /mon-equipe/* (aucun chevauchement de prefixe). */}
              <Route path="/planification-salariale" element={<PlanificationPage />} />
              {/* L05 Equite salariale (F-600) : placeholder CE — le vrai module
                  (analyse comparative, ecarts, plans de correction) est L06+. Aucun
                  item de nav ne pointe cette route ; entree par URL. */}
              <Route path="/equite-salariale" element={<EquiteSalarialePage />} />
              <Route path="/error/:code" element={<GsErrorPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </GsErrorBoundary>
        </GsTooltipProvider>
      </GsLayout>
      {/* Tiroir « Plus » mobile (bottom sheet §4c) — surplus de la barre 4 onglets. */}
      <MoreMenuSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        items={moreItems}
        onNavigate={(path: string) => navigate(path)}
        title={t('gest:nav.more')}
        closeLabel={t('gest:nav.closeMore')}
      />
    </BrandingProvider>
  );
}
